import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "react-hot-toast";
import { FaSpinner } from "react-icons/fa";
import InstallButton from "./InstallButton";
import { login } from "../actions/authActions";
import logo from "../assets/logo.png";
import "../style/Login.css";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { email, password } = formData;

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const onChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("ایمیل و رمز عبور الزامی است.", {
        id: "login-validation",
      });
      return;
    }
    if (!validateEmail(email)) {
      toast.error("ایمیل وارد شده معتبر نیست.", {
        id: "login-validation",
      });
      return;
    }
    if (password.length < 6) {
      toast.error("رمز عبور باید حداقل ۶ کاراکتر باشد.", {
        id: "login-validation",
      });
      return;
    }
    try {
      setIsLoading(true);
      await dispatch(login({ email, password }));
    } catch (error) {
      setIsLoading(false);
      toast.error(auth.error?.detail || "ورود ناموفق. لطفا دوباره تلاش کنید.", {
        id: "login-error",
      });
    }
  };

  useEffect(() => {
    if (auth.isAuthenticated) {
      toast.success("ورود موفق؛ در حال انتقال به داشبورد...", {
        id: "login-success",
      });
      const timer = setTimeout(() => {
        setIsLoading(false);
        navigate("/");
      }, 1500);
      return () => clearTimeout(timer);
    }
    if (auth.error) {
      setIsLoading(false);
      toast.error(auth.error.detail || "ورود ناموفق بود.", {
        id: "login-error",
      });
    }
  }, [auth.isAuthenticated, auth.error, navigate]);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4 sm:px-6 lg:px-10 py-10"
      dir="rtl"
    >
      <div className="w-full max-w-3xl mx-auto">
        <div className="relative w-full bg-white/95 backdrop-blur-xl border border-white/40 shadow-2xl rounded-3xl overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -left-24 h-64 w-64 bg-gradient-to-br from-sky-500/50 via-indigo-500/40 to-purple-500/30 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-28 -right-10 h-56 w-56 bg-gradient-to-tr from-emerald-400/30 to-sky-500/30 blur-3xl" />

          <div className="relative z-10 p-6 sm:p-8 lg:p-10 flex flex-col gap-6">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center shadow-sm">
                  <img
                    src={logo}
                    alt="لوگو نرم افزار"
                    className="h-8 w-8 object-contain"
                  />
                </div>
                <div className="text-right">
                  <p className="text-xs tracking-[0.25em] text-slate-500">
                    OKIYAN SYSTEM
                  </p>
                  <p className="text-lg font-bold text-slate-900">
                    ورود به پنل بازرگانی
                  </p>
                </div>
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                نسخه امن
              </span>
            </div>

            <form onSubmit={onSubmit} className="space-y-5">
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={onChange}
                  required
                  placeholder="ایمیل "
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200 transition"
                />
              </div>

              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={password}
                  onChange={onChange}
                  required
                  placeholder="رمز عبور"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-4 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200 transition"
                />
                <span
                  className="absolute left-4 top-1/2 -translate-y-1/2 cursor-pointer text-slate-500 hover:text-slate-700"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </span>
              </div>

              <div className="flex items-center justify-between text-sm text-slate-600 gap-3 flex-wrap">
                <label
                  htmlFor="rememberMe"
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    id="rememberMe"
                    className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                  />
                  مرا به خاطر بسپار
                </label>
              </div>

              <div className="flex flex-col gap-3">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full rounded-2xl bg-gradient-to-l from-sky-600 to-indigo-600 text-white py-3 font-semibold shadow-lg hover:shadow-xl transition disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <FaSpinner className="animate-spin" size={18} />
                      <span>در حال ورود...</span>
                    </>
                  ) : (
                    "ورود به حساب"
                  )}
                </button>

                <div className="flex items-center justify-between flex-wrap gap-3 text-xs text-slate-500">
                  <span>نیاز به نصب روی موبایل؟</span>
                  <InstallButton forceShow />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
